import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { CSSObject, WithStyles } from "type/tss";
import { Theme } from "@mui/material/styles";

export type FieldHelperVariant = "info" | "loading" | "error" | "success";

const styles = (theme: Theme) => {
	return {
		root: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textSecondary,
			"& svg": {
				flexShrink: 0,
			},
		},
		info: {
			color: theme.palette.custom.textSecondary,
		},
		loading: {
			color: theme.palette.custom.textSecondary,
		},
		error: {
			color: theme.palette.error.main,
		},
		success: {
			color: theme.palette.custom.success,
		},
	} satisfies Record<string, CSSObject>;
};

const icons: Record<FieldHelperVariant, Icons.Icon> = {
	info: Icons.Info,
	loading: Icons.Loader,
	error: Icons.XCircle,
	success: Icons.CheckCircle,
};

interface Props extends WithStyles<typeof styles> {
	variant: FieldHelperVariant;
	className?: string;
	children: React.ReactNode;
}

/** Figma's field "Helper Text": a 16px status icon beside neutral body copy. */
const Component: React.FC<Props> = ({ classes = {}, variant, className, children }) => {
	const Icon = icons[variant];
	return (
		<Mui.Typography
			component="div"
			className={[classes.root, className].filter(Boolean).join(" ")}
			role={variant === "error" ? "alert" : undefined}
		>
			<Icon size={16} className={classes[variant]} aria-hidden="true" />
			{children}
		</Mui.Typography>
	);
};

export const FieldHelper = withStyles(Component, styles);
